/**
 * CostsTableRow - Linha da tabela de custos
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Chip,
  Checkbox,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { formatTime } from '../../common/util/formatter';
import CollectionActions from './CollectionActions';

// Helper de formatação de moeda
const formatCurrency = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CostsTableRow = memo(({
  row,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const tokens = usePremiumTokens();

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${tokens?.accent?.primary}20`,
            color: tokens?.accent?.primary,
          }}
        >
          <ReceiptIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Category/Type */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={row.type || '-'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            backgroundColor: tokens?.semantic?.infoBg,
            color: tokens?.semantic?.info,
            border: `1px solid ${tokens?.semantic?.info}40`,
          }}
        />
      </TableCell>

      {/* Price */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={formatCurrency(row.price)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: tokens?.semantic?.successBg,
            color: tokens?.semantic?.success,
            border: `1px solid ${tokens?.semantic?.success}40`,
          }}
        />
      </TableCell>

      {/* Date */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {formatTime(row.eventTime, 'date')}
        </Typography>
      </TableCell>

      {/* Description */}
      <TableCell sx={{ py: 1, maxWidth: 200 }}>
        <Typography
          variant="body2"
          sx={{ color: tokens?.text?.muted, fontSize: '0.8rem' }}
          noWrap
        >
          {row.description || '-'}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/cost"
          endpoint="costs"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

CostsTableRow.displayName = 'CostsTableRow';

export default CostsTableRow;
